import * as Sentry from '@sentry/react';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export const getLocalStorageItem = <T>(
  key: string,
  defaultValue: T = {} as T,
): T => {
  const localStoreValue = localStorage.getItem(key);
  return localStoreValue ? (JSON.parse(localStoreValue) as T) : defaultValue;
};

export const addLocalStorageItem = (
  key: string,
  value: Record<string, unknown>,
): void => {
  localStorage.setItem(
    key,
    JSON.stringify({
      ...getLocalStorageItem(key),
      ...value,
    }),
  );
};

export const setLocalStorageItem = (
  key: string,
  value: Record<string, unknown>,
): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

type SetValue<T> = Dispatch<SetStateAction<T>>;
type JsonValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JsonValue }
  | JsonValue[];

const isSetStateAction = <T>(
  value: SetStateAction<T>,
): value is (prevState: T) => T => {
  return typeof value === 'function';
};

// Provides hook that persist the state with local storage
// https://usehooks-ts.com/react-hook/use-local-storage
export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [T, SetValue<T>] => {
  const initialValueRef = useRef(initialValue);

  const readValue = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initialValueRef.current;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        return initialValueRef.current;
      }

      const parsed = parseJSON(item);
      return parsed === undefined ? initialValueRef.current : (parsed as T);
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      Sentry.captureException(error);
      return initialValueRef.current;
    }
  }, [key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue: SetValue<T> = useCallback(
    (value) => {
      try {
        setStoredValue((currentValue) => {
          const newValue = isSetStateAction(value)
            ? value(currentValue)
            : value;

          if (typeof window !== 'undefined') {
            window.localStorage.setItem(key, JSON.stringify(newValue));
          }

          return newValue;
        });
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error);
        Sentry.captureException(error);
      }
    },
    [key],
  );

  useEffect(() => {
    setStoredValue(readValue());
  }, [readValue]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key !== key) {
        return;
      }

      setStoredValue(readValue());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, readValue]);

  return [storedValue, setValue];
};

const parseJSON = (value: string | null): JsonValue | undefined => {
  try {
    if (!value) {
      return undefined;
    }

    const parsed = JSON.parse(value) as JsonValue;
    return parsed;
  } catch (error) {
    console.error('parsing error on', { value });
    Sentry.captureException(error);
    return undefined;
  }
};
